<template>
	<!-- Root: flex col so footer is always at the bottom -->
	<div
		class="min-h-screen flex flex-col font-['Inter',sans-serif] transition-colors duration-300"
		:class="isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]'">
		<!-- Imigongo background patterns -->
		<div
			class="fixed inset-0 pointer-events-none overflow-hidden"
			aria-hidden="true">
			<svg
				class="absolute -top-10 -right-10 w-[420px] h-[420px]"
				:style="{ opacity: isLight ? 0.07 : 0.06 }"
				viewBox="0 0 160 160"
				fill="none"
				xmlns="http://www.w3.org/2000/svg">
				<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
					<rect
						x="40"
						y="0"
						width="28"
						height="28"
						transform="rotate(45 40  0)" />
					<rect
						x="80"
						y="0"
						width="28"
						height="28"
						transform="rotate(45 80  0)" />
					<rect
						x="120"
						y="0"
						width="28"
						height="28"
						transform="rotate(45 120 0)" />
					<rect
						x="20"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 20  40)" />
					<rect
						x="60"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 60  40)" />
					<rect
						x="100"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 100 40)" />
					<rect
						x="140"
						y="40"
						width="28"
						height="28"
						transform="rotate(45 140 40)" />
					<rect
						x="40"
						y="80"
						width="28"
						height="28"
						transform="rotate(45 40  80)" />
					<rect
						x="80"
						y="80"
						width="28"
						height="28"
						transform="rotate(45 80  80)" />
					<rect
						x="120"
						y="80"
						width="28"
						height="28"
						transform="rotate(45 120 80)" />
					<rect
						x="20"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 20  120)" />
					<rect
						x="60"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 60  120)" />
					<rect
						x="100"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 100 120)" />
					<rect
						x="140"
						y="120"
						width="20"
						height="20"
						transform="rotate(45 140 120)" />
				</g>
			</svg>
			<svg
				class="absolute -bottom-10 -left-10 w-[280px] h-[280px]"
				:style="{ opacity: isLight ? 0.06 : 0.05 }"
				viewBox="0 0 120 120"
				fill="none"
				xmlns="http://www.w3.org/2000/svg">
				<g
					:stroke="isLight ? '#0f1f1c' : '#a8d5cc'"
					stroke-width="3"
					fill="none">
					<rect
						x="60"
						y="10"
						width="64"
						height="64"
						transform="rotate(45 60 10)" />
					<rect
						x="60"
						y="22"
						width="46"
						height="46"
						transform="rotate(45 60 22)" />
					<rect
						x="60"
						y="34"
						width="28"
						height="28"
						transform="rotate(45 60 34)" />
					<rect
						x="60"
						y="46"
						width="10"
						height="10"
						transform="rotate(45 60 46)" />
				</g>
			</svg>
		</div>

		<!-- flex-grow centres the card vertically between top and footer -->
		<div
			class="relative flex-grow flex flex-col items-center justify-center px-4 py-16 gap-5">
			<!-- Back home -->
			<router-link
				to="/"
				class="inline-flex items-center gap-2 text-[11px] font-semibold tracking-widest uppercase border rounded-full px-4 py-1.5 transition-all duration-200"
				:class="
					isLight
						? 'text-[#0d6b5e] bg-[#1a9b8a]/10 border-[#1a9b8a]/30 hover:bg-[#1a9b8a]/20'
						: 'text-[#a8d5cc] bg-[#1a9b8a]/10 border-[#1a9b8a]/25 hover:bg-[#1a9b8a]/20'
				">
				<svg
					fill="none"
					viewBox="0 0 24 24"
					stroke-width="2"
					stroke="currentColor"
					class="w-3 h-3">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M15.75 19.5L8.25 12l7.5-7.5" />
				</svg>
				{{ $t("unsubscribe.backHome") }}
			</router-link>

			<!-- Card -->
			<div
				class="w-full max-w-md rounded-2xl border overflow-hidden shadow-2xl shadow-black/40"
				:class="
					isLight
						? 'border-[#1a9b8a]/15 bg-white/60'
						: 'border-white/[0.07] bg-white/[0.03]'
				">
				<!-- Top teal band -->
				<div
					class="h-1.5 w-full bg-gradient-to-r from-[#1a9b8a] to-[#0d6b5e]"></div>

				<div class="px-8 py-8">
					<!-- Brand mark -->
					<div class="flex justify-center mb-6">
						<div
							class="w-12 h-12 rounded-xl flex items-center justify-center"
							:class="isLight ? 'bg-[#1a9b8a]/15' : 'bg-[#1a9b8a]/15'">
							<svg
								class="w-6 h-6"
								:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="1.5"
									d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
							</svg>
						</div>
					</div>

					<!-- Title -->
					<h2
						class="font-['Montserrat'] font-extrabold text-xl tracking-tight text-center mb-1"
						:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
						{{ $t("unsubscribe.title") }}
					</h2>
					<p
						class="text-[11px] text-center tracking-wide mb-8"
						:class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/35'">
						{{ $t("unsubscribe.subtitle") }}
					</p>

					<!-- STEP 1 -->
					<div v-if="step === 1">
						<p
							class="text-sm text-center mb-4"
							:class="isLight ? 'text-[#0f1f1c]/55' : 'text-white/50'">
							{{ $t("unsubscribe.step1Desc") }}
						</p>
						<div
							class="rounded-xl border px-4 py-3 text-sm text-center font-medium mb-6 tracking-wide"
							:class="
								isLight
									? 'border-[#1a9b8a]/20 bg-[#1a9b8a]/5 text-[#0f1f1c]/80'
									: 'border-white/[0.08] bg-white/[0.04] text-white/80'
							">
							{{ email }}
						</div>
						<button
							@click="sendOtp"
							:disabled="!email || isLoading"
							class="w-full py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed bg-[#1a9b8a] text-white hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20">
							<svg
								v-if="isLoading"
								class="w-4 h-4 animate-spin"
								fill="none"
								viewBox="0 0 24 24">
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4" />
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
							</svg>
							{{
								isLoading
									? $t("unsubscribe.sending")
									: $t("unsubscribe.sendOtp")
							}}
						</button>
					</div>

					<!-- STEP 2 -->
					<div v-if="step === 2">
						<p
							class="text-sm text-center mb-4"
							:class="isLight ? 'text-[#0f1f1c]/55' : 'text-white/50'">
							{{ $t("unsubscribe.step2Desc") }}
						</p>
						<input
							v-model="otp"
							type="text"
							:placeholder="$t('unsubscribe.otpPlaceholder')"
							class="w-full px-4 py-3.5 rounded-xl border text-sm text-center font-mono tracking-[0.35em] focus:outline-none transition-all duration-200 mb-6"
							:class="
								isLight
									? 'bg-white border-[#1a9b8a]/20 text-[#0f1f1c] placeholder-[#0f1f1c]/25 focus:border-[#1a9b8a]/50'
									: 'bg-white/[0.04] border-white/[0.08] text-white placeholder-white/20 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'
							" />
						<button
							@click="verifyOtp"
							:disabled="!otp || isLoading"
							class="w-full py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/20">
							<svg
								v-if="isLoading"
								class="w-4 h-4 animate-spin"
								fill="none"
								viewBox="0 0 24 24">
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4" />
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
							</svg>
							{{
								isLoading
									? $t("unsubscribe.verifying")
									: $t("unsubscribe.verifyOtp")
							}}
						</button>
					</div>

					<!-- SUCCESS -->
					<div v-if="step === 3" class="flex flex-col items-center gap-4 py-2">
						<div
							class="w-14 h-14 rounded-full flex items-center justify-center"
							:class="isLight ? 'bg-[#1a9b8a]/15' : 'bg-[#1a9b8a]/15'">
							<svg
								class="w-7 h-7"
								:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M5 13l4 4L19 7" />
							</svg>
						</div>
						<p
							class="font-['Montserrat'] font-bold text-lg text-center"
							:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
							{{ $t("unsubscribe.successTitle") }}
						</p>
						<p
							class="text-sm text-center leading-relaxed"
							:class="isLight ? 'text-[#0f1f1c]/45' : 'text-white/40'">
							{{ $t("unsubscribe.successDesc") }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer pinned at bottom via flex-col -->
		<FooterComponent :isLight="isLight" @toggle-mode="toggleMode" />

		<!-- CONFIRM MODAL -->
		<transition name="fade">
			<div
				v-if="showConfirm"
				class="fixed inset-0 z-[9999] flex items-center justify-center px-4"
				@click.self="showConfirm = false">
				<div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
				<div
					class="relative w-full max-w-sm rounded-2xl border shadow-2xl p-6 z-10"
					:class="
						isLight
							? 'bg-white border-[#1a9b8a]/15'
							: 'bg-[#132420] border-white/[0.08]'
					">
					<div
						class="w-11 h-11 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
						<svg
							class="w-5 h-5 text-red-400"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
						</svg>
					</div>
					<h3
						class="font-['Montserrat'] font-bold text-lg mb-2"
						:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
						{{ $t("unsubscribe.confirmTitle") }}
					</h3>
					<p
						class="text-sm leading-relaxed mb-6"
						:class="isLight ? 'text-[#0f1f1c]/55' : 'text-white/50'">
						{{ $t("unsubscribe.confirmDesc") }}
					</p>
					<div class="flex gap-3">
						<button
							@click="showConfirm = false"
							class="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200"
							:class="
								isLight
									? 'border-black/10 text-[#0f1f1c]/50 hover:text-[#0f1f1c] hover:border-black/20'
									: 'border-white/[0.08] text-white/50 hover:text-white hover:border-white/20'
							">
							{{ $t("unsubscribe.confirmCancel") }}
						</button>
						<button
							@click="unsubscribeNewsLetters"
							class="flex-1 py-2.5 rounded-xl text-sm font-bold bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/20 transition-all duration-200 flex items-center justify-center gap-2">
							<svg
								v-if="isLoading"
								class="w-4 h-4 animate-spin"
								fill="none"
								viewBox="0 0 24 24">
								<circle
									class="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									stroke-width="4" />
								<path
									class="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
							</svg>
							{{
								isLoading
									? $t("unsubscribe.verifying")
									: $t("unsubscribe.confirmYes")
							}}
						</button>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import FooterComponent from "@/components/FooterComponent.vue";
import { useTheme } from "@/composables/useTheme";
import apiService from "@/services/api";
import { useToast } from "vue-toastification";

export default {
	name: "UnsubscribeView",
	components: { FooterComponent },
	setup() {
		const toast = useToast();
		const { isLight, toggleMode } = useTheme();
		return { toast, isLight, toggleMode };
	},
	data() {
		return {
			email: "",
			otp: "",
			step: 1,
			isLoading: false,
			showConfirm: false,
		};
	},
	mounted() {
		const emailFromUrl = this.$route.query.email;
		if (emailFromUrl) {
			this.email = decodeURIComponent(emailFromUrl);
		}
	},
	methods: {
		async sendOtp() {
			try {
				this.isLoading = true;
				await apiService.sendOtp(this.email);
				this.step = 2;
			} catch (error) {
				console.error("Error sending OTP:", error);
				this.toast.error(
					`Failed to send OTP to ${this.email}. Please try again.`,
				);
			} finally {
				this.isLoading = false;
			}
		},
		async verifyOtp() {
			try {
				this.isLoading = true;
				await apiService.verifyOtp(this.email, this.otp);
				this.showConfirm = true;
			} catch (error) {
				console.error("Error verifying OTP:", error);
				this.toast.error(`Invalid OTP. Please try again.`);
			} finally {
				this.isLoading = false;
			}
		},
		async unsubscribeNewsLetters() {
			try {
				this.isLoading = true;
				await apiService.unsubscribeNewsLetter(this.email);
				this.toast.success(`${this.email} has been unsubscribed successfully!`);
				this.step = 3;
			} catch (error) {
				console.error("Error unsubscribing:", error);
				this.toast.error(
					`Failed to unsubscribe ${this.email}. Please try again.`,
				);
			} finally {
				this.isLoading = false;
				this.showConfirm = false;
			}
		},
	},
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
